import {
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NoteService } from 'app/shared/services/note.service';
import { INote, IParentEntity } from 'app/shared/interfaces/entity';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import {
  MessageBoxComponent,
  MessageBoxParameters,
  MessageBoxType,
} from 'app/components/messageBox/messageBox.component';
import { debounceTime, take } from 'rxjs/operators';
import { UserService } from 'app/shared/services/user.service';
import { AuthService } from 'app/shared/services/auth.service';
import { NoteType } from 'app/shared/models/note';
import { NoteTypeService } from 'app/shared/services/note-type.service';
import { SubjectService } from 'app/shared/services/subject.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { SubjectUserSecurity } from 'app/shared/models/subject';

@AutoUnsubscribe()
@Component({
  selector: 'note-dialog',
  templateUrl: './note-dialog.component.html',
  styleUrls: ['./note-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NoteDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  note: INote;
  parent: IParentEntity;
  readOnly = false;
  draftSaved = false;
  closing = false;
  editor: any;
  originalValues: any;
  hasChanges = false;
  title: string;
  userReadOnly = false;
  hasSubjectUserEditPrivs = false;
  selectedNoteType: NoteType = null;
  noteTypes: NoteType[] = [];
  noteTypesList: NoteType[] = [];
  subjectTypeId: number = -1;
  showNoteType: boolean = false;
  content: string;

  saveChanges: ReplaySubject<any> = new ReplaySubject<any>(1, 100);
  onSaveChanges: Subscription;
  onCurrentNoteChanged: Subscription;
  onNoteTypesChanged: Subscription;
  debouncer: Subject<any> = new Subject();
  disableAutoSave: boolean = false;

  constructor(
    private noteService: NoteService,
    private userService: UserService,
    private authService: AuthService,
    private noteTypeService: NoteTypeService,
    private subjectService: SubjectService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<NoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.note = data.note;
    this.parent = data.parent;
    this.readOnly =
      data.readOnly ||
      (data.note.note.createdById &&
        data.note.note.createdById !== this.userService.getCurrentUserId());
    this.hasChanges = false;
    this.originalValues = {
      html: data.note.note.detail,
      starred: data.note.note.starred,
      important: data.note.note.important,
      sensitive: data.note.note.sensitive,
      noteTypeId: data.note.note.noteTypeId,
    };
    this.content = data.note.note.detail;
    this.userReadOnly = this.authService.isUserReadOnly();
    this.subjectService
      .getSubjectUserSecurity(this.subjectService.currentItem, this.userService.getCurrentUserId())
      .subscribe((result: SubjectUserSecurity) => {
        if (!result || !result.restrictAccess || result.canEdit) {
          this.hasSubjectUserEditPrivs = true;
        }
      });
  }

  @HostListener('window:keyup.esc') onKeyUp() {
    this.dialogRef.close();
  }

  ngOnInit() {
    this.onNoteTypesChanged = this.noteTypeService.onChanged.subscribe((noteTypeList: NoteType[]) => {
      this.noteTypesList = noteTypeList
        .filter((i) => i.hideRecord === undefined || i.hideRecord === false)
        .sort((a, b) => a.name.localeCompare(b.name));
      this.updateNoteTypes();
    });
    this.noteTypeService.getAll();

    // set dialog title
    if (this.note && this.note.note.id) {
      if (this.note.sequenceId) {
        this.title = `View Note - ${this.note.sequenceId}`;
      } else {
        this.title = this.readOnly ? 'View Note' : 'Edit Note';
      }
    } else {
      this.title = 'Add Note';
    }

    // make sure that the current note is correctly set but as we have already set the correct note here don't bother to notify listeners at this stage
    this.noteService.setCurrentNote(this.note.uid, false);

    // setup a debouncer for the auto note save.  Should fire approx 5000 milliseconds or so after user has paused making changes
    this.debouncer
      .pipe(debounceTime(5000))
      .subscribe((_) => {
        try {
          // If disableAutoSave is true, do not save here. DisableAutoSave is set to true when the user clicks a save button and reset after the save has taken place.
          // If the Autosave is triggered within 500ms of the user clicking the save and lock button the save will be the Auto Save which is a draft save, not the user save.
          if (this.disableAutoSave === true)
          {
            console.log("Note AutoSave disabled because user initiated save is taking place.");
            return;            
          }
          console.log("AutoSaving Note");
          this.doSaveNote(true, this.parent, "Note auto saved", false);
        } catch (err) { }
      });

    // subscribe to save changes
    this.onSaveChanges = this.saveChanges
      .pipe(
        debounceTime(500) // 0.5 second debounce
      )
      .subscribe((data) => {
        this.noteService.saveNote(
          data.note,
          data.parent,
          data.draft,
          data.message
        );
      });

    this.subjectService
      .get(this.parent.uid)
      .pipe(take(1))
      .subscribe((subject) => {
        if (subject) {
          this.subjectTypeId = subject.subjectTypeId;
          this.updateNoteTypes();
        }
      });

    this.onCurrentNoteChanged =
      this.noteService.onCurrentNoteChanged.subscribe((note: INote) => {
        if (
          !this.readOnly &&
          note &&
          note.note &&
          (note.id === this.note.id || (!this.note.id && this.draftSaved))
        ) {
          this.note = note;
        }
      });
  }

  ngOnDestroy() {
    if (this.onCurrentNoteChanged) this.onCurrentNoteChanged.unsubscribe();
    if (this.onSaveChanges) this.onSaveChanges.unsubscribe();
    if (this.onNoteTypesChanged) this.onNoteTypesChanged.unsubscribe();
  }

  ngAfterViewInit(): void {
    if (!this.selectedNoteType) {
      this.updateNoteTypes();
    }
  }

  updateNoteTypes() {
    this.noteTypes = [];
    this.showNoteType = false;
    this.noteTypesList.forEach((i) => {
      if ((i.subjectTypeId == undefined || i.subjectTypeId === -1) || (i.subjectTypeId === this.subjectTypeId)) {
        this.noteTypes.push(i);
        this.showNoteType = true;
      }
    });
    this.selectedNoteType = this.noteTypes.find(
      (i) => i.id === this.note.note.noteTypeId
    );
  }

  onNoteTypeChanged(e) {
    this.note.note.noteTypeId = e;
    this.selectedNoteType = this.noteTypes.find((i) => i.id === e);
    this.hasChanges = this.hasContent;
  }

  get hasContent(): boolean {
    return this.note.note.detail > '';
  }

  // save draft button event handler
  savedraft() {
    this.saveNote(true, true);
  }

  // save and lock event handler
  public confirmSubmit(): void {
    this.closing = true;
    this.disableAutoSave = true;
    this.saveNote(false, true);
  }

  private saveNote(draft: boolean, close: boolean) {
    try {
      if (close || this.hasChanges) {
        this.doSaveNote(draft, this.parent, undefined, close);
      }
    } catch (err) { }
  }

  private doSaveNote(draft: boolean, parent?: IParentEntity, message?: string, close?: boolean) {
    try {
      if (close && close===true) {
        this.closing = true;
        this.noteService.onAfterSave
          .pipe(take(1))
          .subscribe((_) => {
            this.dialogRef.close();
            this.disableAutoSave = false;
          });
      }
      else {
        this.noteService.onAfterSave
          .pipe(take(1))
          .subscribe((_) => {
            this.disableAutoSave = false;
          });
      }

      // trigger save of note
      this.saveChanges.next({
        note: this.note,
        parent,
        draft: draft === true,
        message:
          message || ((draft === true) ? 'Note saved as draft' : 'Note saved and locked'),
      });
      this.draftSaved = true;
    } catch (err) { }
  }

  // close button event handler
  saveAndClose() {
    this.closing = true;
    let prompt = '';
    let title = '';

    try {

      // if has no changes but content not the same then changes have been auto saved
      // check if want to discard
      if (
        this.note &&
        this.note.id &&
        !this.hasChanges &&
        this.note.note &&
        this.note.note.detail !== this.originalValues.html
      ) {
        prompt = 'Your changes have been automatically saved. Discard?';
        title = 'Changes have been saved';
      }

      // if has changes and there is content then there are unsaved changes
      if (this.hasContent && this.hasChanges) {
        prompt = 'You have unsaved changes.  Discard?';
        title = 'Unsaved changes';
      }

      if (prompt) {
        const info = new MessageBoxParameters(
          prompt,
          title,
          MessageBoxType.YesNo
        );
        this.dialog
          .open(MessageBoxComponent, { data: info })
          .afterClosed()
          .subscribe((result) => {
            if (result === 'yes') {
              if (this.originalValues.html === '') {
                if (this.note && this.note.id) {
                  this.noteService
                    .deleteNote(this.parent, this.note)
                    .subscribe();
                } else {
                  // do nothing - if no id note hasn't been saved yet so we don't need to delete!
                }
                this.dialogRef.close();
              } else {
                if (this.note && this.note.note) {
                  this.note.note.detail = this.originalValues.html;
                  this.note.note.starred = this.originalValues.starred;
                  this.note.note.sensitive = this.originalValues.sensitive;
                  this.note.note.important = this.originalValues.important;
                  this.note.note.noteTypeId = this.originalValues.noteTypeId;

                  if (!this.readOnly) {
                    this.doSaveNote(
                      true,
                      this.parent,
                      'Changes have been discarded',
                      true
                    );
                  } else {
                    this.dialogRef.close();
                  }
                } else {
                  this.dialogRef.close();
                }
              }
            } else {
              if (!this.readOnly) {
                this.saveNote(true, false);
                this.closing = false;
              }
            }
          });
      } else {
        // check if we have flag changes for readonly note - if we do, then we need to make sure changes are persisted
        if (this.hasChanges && this.note && this.note.sequenceId) {
          this.doSaveNote(false, this.parent, 'Note saved', true);
        } else {
          this.dialogRef.close();
        }
      }
    } catch (err) {
      // if something goes wrong, close the dialog anyway
      this.dialogRef.close();
    }
  }

  // save changes button event handler
  private saveContentChange() {
    this.doSaveNote(
      false,
      this.hasChanges ? this.parent : undefined,
      'Note changes saved',
      true
    );
  }

  // note editor change event handler
  contentChanged(content) {
    if (!this.closing && (Object.keys(content).length !== 0)) {
      this.hasChanges = true;
      this.note.note.detail = content;
      this.debouncer.next(content);
    }
  }


  //     // disable keyboard bindings to prevent delete, enter, backspace etc
  // const quill = new DevExpress.Quill('#editor', {
  //   modules: {
  //     keyboard: {
  //       bindings: {}
  //     }
  //   }
  // });
  //     const keyboard = this.editor.getModule('keyboard');
  //     keyboard.bindings = {};
  //   }


  toggleStar(event) {
    event.stopPropagation();
    if (this.note && this.note.note) {
      this.note.note.toggleStar();
      this.hasChanges = this.hasContent;
    }
  }

  toggleImportant(event) {
    event.stopPropagation();
    if (this.note && this.note.note) {
      this.note.note.toggleImportant();
      this.hasChanges = this.hasContent;
    }
  }

  toggleSensitive(event) {
    event.stopPropagation();
    if (this.note && this.note.note) {
      this.note.note.toggleSensitive();
      this.hasChanges = this.hasContent;
    }
  }

  openWindow(event) {
    event.stopPropagation();
    const win = window.open(
      `notes/new/subject/${this.parent.uid}/notetype/subject`,
      '_blank'
    );
    win.focus();
  }
}
